import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Smartphone, MapPin, CreditCard, Clock, Zap, Battery, Gauge,
  CheckCircle2, ArrowRight, Gift, Bell,
  Wallet, History
} from 'lucide-react';

const IndividualsPageEN = () => {
  const appFeatures = [
    {
      icon: MapPin,
      title: 'Find the Nearest Station',
      description: 'Interactive map with all charging stations. Filter by charger type and availability.'
    },
    {
      icon: Zap,
      title: 'Start Charging with One Click',
      description: 'Scan the QR code or select the charger from the app. Charging starts immediately.'
    },
    {
      icon: CreditCard,
      title: 'Easy Payment',
      description: 'Link your bank card and pay automatically. No cash needed.'
    },
    {
      icon: History,
      title: 'Complete History',
      description: 'View all previous charges, consumption, and invoices in one place.'
    },
    {
      icon: Bell,
      title: 'Real-Time Notifications',
      description: 'Get notified when charging is complete or when your favorite station becomes available.'
    },
    {
      icon: Wallet,
      title: 'Digital Wallet',
      description: 'Add balance and benefit from special offers. Even faster payments.'
    }
  ];

  const benefits = [
    'Charge at any AlbMove station in Albania',
    'Transparent pricing with no hidden costs',
    '24/7 support through the app',
    'Exclusive offers and promotions',
    'Detailed charging history',
    'Automatic electronic invoices'
  ];

  const steps = [
    { num: '01', title: 'Download the App', desc: 'Available free on App Store and Google Play' },
    { num: '02', title: 'Create an Account', desc: 'Register with email and link your bank card' },
    { num: '03', title: 'Find a Station', desc: 'Use the map to find the nearest station' },
    { num: '04', title: 'Charge Up!', desc: 'Scan the QR code and start charging' }
  ];

  return (
    <div data-testid="individuals-page-en" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-lime-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-blue-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Smartphone className="w-4 h-4 text-lime-400" />
                <span className="text-sm font-medium text-blue-100">For Individual Users</span>
              </div>
              
              <h1 
                data-testid="individuals-title"
                className="text-4xl md:text-6xl font-bold tracking-tight mb-6"
              >
                Car Charging<br />
                <span className="text-lime-400">Made Simple</span>
              </h1>
              
              <p className="text-xl text-blue-100 leading-relaxed mb-8 max-w-lg">
                With the AlbMove app, find the nearest station, start charging, and pay - 
                all from your phone.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://apps.apple.com/hr/app/albmove/id6759310357"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="app-store-btn"
                  className="inline-flex items-center gap-3 bg-white text-slate-900 rounded-xl px-6 py-4 font-medium hover:bg-lime-100 transition-colors"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs opacity-70">Download on</p>
                    <p className="font-bold">App Store</p>
                  </div>
                </a>
                <a 
                  href="https://play.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="play-store-btn"
                  className="inline-flex items-center gap-3 bg-white text-slate-900 rounded-xl px-6 py-4 font-medium hover:bg-lime-100 transition-colors"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs opacity-70">Get it on</p>
                    <p className="font-bold">Google Play</p>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Phone Mockup */}
            <div className="relative hidden lg:flex items-center justify-center min-h-[500px]">
              {/* Samsung mockup - back */}
              <img 
                src="/images/mockups/samsung-mockup.png"
                alt="AlbMove Samsung app"
                className="absolute w-[60%] max-w-[300px] -translate-x-[35%] translate-y-6 drop-shadow-2xl z-0"
                data-testid="individuals-mockup-samsung-en"
              />
              {/* iPhone mockup - front */}
              <img 
                src="/images/mockups/iphone-mockup.png"
                alt="AlbMove iPhone app"
                className="relative w-[60%] max-w-[300px] translate-x-[35%] drop-shadow-2xl z-10"
                data-testid="individuals-mockup-iphone-en"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="how-it-works" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
              How It Works?
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              4 Simple Steps
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div 
                key={index}
                data-testid={`step-${index}`}
                className="text-center group"
              >
                <div className="w-20 h-20 rounded-2xl bg-blue-100 group-hover:bg-blue-600 flex items-center justify-center mx-auto mb-6 transition-colors">
                  <span className="text-3xl font-bold text-blue-600 group-hover:text-white transition-colors">
                    {step.num}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Features */}
      <section data-testid="app-features" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
              Features
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Everything in the App
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {appFeatures.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-${index}`}
                className="bg-white p-8 rounded-3xl border border-slate-200/50 hover:border-blue-200 hover:shadow-xl transition-all group"
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-100 group-hover:bg-blue-600 flex items-center justify-center mb-6 transition-colors">
                  <feature.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
                Benefits
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-8">
                Why Choose AlbMove?
              </h2>
              
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-lime-500 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-10 text-white">
              <Gift className="w-14 h-14 text-lime-400 mb-6" />
              <h3 className="text-2xl font-bold mb-4">
                Special Offer for New Users!
              </h3>
              <p className="text-blue-100 text-lg mb-6">
                Register now and get €10 free credit for your first charge.
              </p>
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
              >
                <a href="https://play.google.com/store/apps/details?id=com.albmove.client" target="_blank" rel="noopener noreferrer">
                  Download Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-lime-400 to-lime-500 rounded-3xl p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-slate-700 mb-8 max-w-xl mx-auto">
              Download the free app and start charging your electric vehicle today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-bold"
              >
                <Link to="/en/stations">
                  Find a Station
                  <MapPin className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-bold"
              >
                <Link to="/en/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndividualsPageEN;
